import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import { TableFooter, TablePagination } from "@mui/material";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import { styled, useTheme } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import React, { Dispatch, SetStateAction } from "react";
import CircleAvatar from "../../../../components/CircleAvatar/CircleAvatar";
import Loader from "../../../../components/Loader/Loader";
import {
  RefoundSession,
  Sessions,
  SessionStatuses,
} from "../../../../types/TypeSession";
import { TypeUserData } from "../../../../types/TypeUsers";
import { formatDateAndTime } from "../../../../utils/dateActions";
import { getTranslation } from "../../../../utils/getTranslation";
import s from "./SessionsTable.module.css";

export type FilteredSession = Omit<Sessions, "invitedUsers" | "specialists"> & {
  invitedUsers: TypeUserData[];
  clients: TypeUserData[];
  specialists: TypeUserData[];
  sessionCost: number;
  clientCost: number;
  specialistPay: number;
};

type TableRowData = RefoundSession | FilteredSession;

interface TablePaginationActionsProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement>,
    newPage: number,
  ) => void;
}

function TablePaginationActions(props: TablePaginationActionsProps) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
        className={s.mobileHidden}
      >
        {theme.direction === "rtl" ? (
          <LastPageIcon className={s.paginationIcon} />
        ) : (
          <FirstPageIcon className={s.paginationIcon} />
        )}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight color="success" className={s.paginationIcon} />
        ) : (
          <KeyboardArrowLeft color="success" className={s.paginationIcon} />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft color="success" className={s.paginationIcon} />
        ) : (
          <KeyboardArrowRight color="success" className={s.paginationIcon} />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
        className={s.mobileHidden}
      >
        {theme.direction === "rtl" ? (
          <FirstPageIcon color="success" className={s.paginationIcon} />
        ) : (
          <LastPageIcon color="success" className={s.paginationIcon} />
        )}
      </IconButton>
    </Box>
  );
}

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#313131",

    color: "white",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    padding: "10px 20px",
    color: "white",
  },
}));

const StyledTableRow = styled(TableRow)(
  ({ rowId, selectedRowId }: { rowId: string; selectedRowId: string }) => ({
    "&:nth-of-type(odd)": {
      backgroundColor: selectedRowId === rowId ? "#1f4e3b" : "#2E2E2E",
      color: "white",
    },
    "&:nth-of-type(even)": {
      backgroundColor: selectedRowId === rowId ? "#1f4e3b" : "#242424",
      border: 0,
      color: "white",
    },
    // hide last border
    "td, th": {
      border: 0,
    },
  }),
);

type BaseProps = {
  rows: TableRowData[];
  page: number;
  rowsPerPage: number;
  setPagination: React.Dispatch<
    React.SetStateAction<{ page: number; rowPerPage: number; total: number }>
  >;
  count: number;
  loader: boolean;
  containerHeight?: string;
};

type RefundProps = BaseProps & {
  variant: "refund";
  onSelectSession: Dispatch<SetStateAction<RefoundSession | undefined>>;
  selectedSession: RefoundSession | undefined;
};

type AllProps = BaseProps & {
  variant: "all";
  selectedSessionId: string | undefined;
  onSelectSessionId: Dispatch<SetStateAction<string | undefined>>;
};

type SessionsTableProps = RefundProps | AllProps;

const isRefundSession = (row: TableRowData): row is RefoundSession =>
  "dateAndTime" in row;

const getStatus = (row: TableRowData): SessionStatuses => {
  if (row.isWaitingForRefund) return SessionStatuses.WAIT_FOR_REFUND;
  if (row.isRefunded) return SessionStatuses.REFUNDED;
  if (row.isCanceled) return SessionStatuses.CANCELED;
  if (row.isWaitingForRefund) return SessionStatuses.WAIT_FOR_REFUND;
  if (row.isPayedOut) return SessionStatuses.COMPLETED;
  return SessionStatuses.PENDING;
};

const statusLabel: Record<SessionStatuses, string> = {
  [SessionStatuses.COMPLETED]: "Completed",
  [SessionStatuses.CANCELED]: "Canceled",
  [SessionStatuses.REFUNDED]: "Refunded",
  [SessionStatuses.WAIT_FOR_REFUND]: "Awaiting refund",
  [SessionStatuses.PENDING]: "Pending",
};

const statusClass: Record<SessionStatuses, string> = {
  [SessionStatuses.COMPLETED]: s.statusCompleted,
  [SessionStatuses.CANCELED]: s.statusCanceled,
  [SessionStatuses.REFUNDED]: s.statusRefunded,
  [SessionStatuses.WAIT_FOR_REFUND]: s.statusWaitingRefund,
  [SessionStatuses.PENDING]: s.statusPending,
};

export default function SessionsTable(props: SessionsTableProps) {
  const { variant, rows, page, rowsPerPage, setPagination, count, loader } =
    props;
  const selectedRowId =
    variant === "refund"
      ? (props.selectedSession?._id ?? "")
      : (props.selectedSessionId ?? "");
  const colSpan = variant === "refund" ? 6 : 6;

  const handleChangePage = (
    _: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setPagination((prev) => ({
      ...prev,
      rowPerPage: parseInt(event.target.value, 10),
    }));
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  return (
    <TableContainer
      component={Paper}
      className={s.tableContainer}
      sx={{
        minWidth: window.innerWidth > 768 ? 0 : "90svw",
        width: "100%",
        borderRadius: "20px",
        backgroundColor: "transparent",
        maxHeight:
          window.innerHeight > 500
            ? props.containerHeight || "calc(100vh - 150px)"
            : "calc(200vh)",
      }}
    >
      <Table
        sx={{
          minWidth: window.innerWidth > 768 ? 0 : "90svw",
          width: "100%",
        }}
        aria-label="sessions table"
        stickyHeader
      >
        <TableHead className={s.tableHead}>
          <TableRow>
            <StyledTableCell align="left">
              <span>Client</span>
            </StyledTableCell>

            <StyledTableCell align="left">
              <span>Specialist</span>
            </StyledTableCell>

            <StyledTableCell align="left">
              <span>Date</span>
            </StyledTableCell>
            <StyledTableCell align="left">
              <span>{"Cost"}</span>
            </StyledTableCell>
            <StyledTableCell align="left">
              <span>{"Specialist pay"}</span>
            </StyledTableCell>
            {variant === "all" && (
              <StyledTableCell align="left">
                <span>{"Status"}</span>
              </StyledTableCell>
            )}
          </TableRow>
        </TableHead>

        <TableBody>
          {loader ? (
            <TableRow>
              <StyledTableCell colSpan={colSpan} align="center">
                <div className={s.loaderContainer}>
                  <Loader size={100} />
                </div>
              </StyledTableCell>
            </TableRow>
          ) : !rows.length ? (
            <TableRow>
              <StyledTableCell colSpan={colSpan} align="center">
                <h2 className={s.noSessions}>No sessions</h2>
              </StyledTableCell>
            </TableRow>
          ) : (
            rows.map((row) => {
              const status = getStatus(row);
              const date = isRefundSession(row)
                ? row.dateAndTime?.date
                : row.datesAndTime?.[0]?.date;
              const dateText = date
                ? `${formatDateAndTime(new Date(date), "en").formattedDate} ${formatDateAndTime(new Date(date), "en").formattedTime}`
                : "-";

              const clients = row.clients?.length
                ? row.clients
                : !isRefundSession(row)
                  ? row.invitedUsers
                  : [];

              return (
                <StyledTableRow
                  key={row._id}
                  onClick={() => {
                    if (variant === "refund" && isRefundSession(row)) {
                      props.onSelectSession(row);
                    }
                    if (variant === "all") {
                      props.onSelectSessionId(row._id);
                    }
                  }}
                  rowId={row._id}
                  selectedRowId={selectedRowId}
                >
                  <StyledTableCell align="left">
                    <div className={s.usersList}>
                      {clients?.length ? (
                        clients.map((item, index) => (
                          <div className={s.avatarBlock} key={index}>
                            <CircleAvatar userId={item._id} />
                            <span>
                              {getTranslation(item.name)}{" "}
                              {getTranslation(item.surname)}
                            </span>
                          </div>
                        ))
                      ) : (
                        <span>No Clients</span>
                      )}
                    </div>
                  </StyledTableCell>

                  <StyledTableCell align="left">
                    <div className={s.usersList}>
                      {row.specialists?.length ? (
                        row.specialists.map((item, index) => (
                          <div className={s.avatarBlock} key={index}>
                            <CircleAvatar userId={item._id} />
                            <span>
                              {getTranslation(item.name)}{" "}
                              {getTranslation(item.surname)}
                            </span>
                          </div>
                        ))
                      ) : (
                        <span>No Specialist</span>
                      )}
                    </div>
                  </StyledTableCell>

                  <StyledTableCell align="left">
                    <div className={s.dateBlock}>{dateText}</div>
                  </StyledTableCell>

                  <StyledTableCell align="left">
                    <div className={s.costBlock}>
                      <span>
                        {(
                          (isRefundSession(row)
                            ? row.clientCost
                            : (row.sessionCost ?? 0)) / 100 || 0
                        ).toFixed(2)}{" "}
                        $
                      </span>
                    </div>
                  </StyledTableCell>
                  <StyledTableCell align="left">
                    <div className={s.costBlock}>
                      <span>{(row.specialistPay / 100 || 0).toFixed(2)} $</span>
                    </div>
                  </StyledTableCell>
                  {variant === "all" && (
                    <StyledTableCell align="left">
                      <span
                        className={`${s.statusBadge} ${statusClass[status]}`}
                      >
                        {statusLabel[status]}
                      </span>
                    </StyledTableCell>
                  )}
                </StyledTableRow>
              );
            })
          )}
        </TableBody>

        <TableFooter className={s.tableFooter}>
          <TableRow
            sx={{
              minWidth: variant === "refund" ? 700 : undefined,
              width: "100%",
              border: "none",
            }}
          >
            <TablePagination
              rowsPerPageOptions={[10, 20, 50]}
              colSpan={colSpan}
              count={count}
              rowsPerPage={rowsPerPage}
              page={page}
              labelRowsPerPage={"Rows per page:"}
              slotProps={{
                select: {
                  inputProps: {
                    "aria-label": "rows per page",
                  },
                  native: true,
                },
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
              sx={{ border: "none", color: "white" }}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
}
